import React, { FC, useEffect } from 'react';
import { getLocalStorage } from 'utils/fnStorage';
import { HelmetData } from 'types/Common';
import { ROBOTS_NOINDEX } from 'utils/fnHelmet';
import seo from 'config/seo.json';

/**
 * Wrapper which received a HelmetData object and based on that updates the meta title, description etc
 *
 * Meta and link tags are rendered inline and hoisted into <head> by React, which also removes them
 * again on unmount. The document title and the <html lang> attribute are set imperatively because
 * React hoists a second <title> rather than replacing the one in the HTML template, and does not
 * manage attributes on <html> at all.
 *
 * Defaults to noindex: a page is only indexable when its builder in `utils/fnHelmet` says so, so a
 * page that renders this wrapper can never leak into the index by forgetting to pass a value.
 *
 * @param title
 * @param canonicalLink
 * @param openGraph
 * @param description
 * @param robots
 * @param jsonLd
 * @param children
 * @constructor
 */
export const HelmetWrapper: FC<HelmetData> = ({
    title,
    canonicalLink,
    openGraph,
    description,
    robots = ROBOTS_NOINDEX,
    jsonLd,
    children,
}) => {
    const appLanguageStorage = getLocalStorage('appLanguage');
    const language = appLanguageStorage || seo.defaultLanguage;
    const twitterCard = openGraph?.['og:image'] ? 'summary_large_image' : 'summary';

    useEffect(() => {
        if (title) {
            document.title = title;
        }
    }, [title]);

    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    return (
        <>
            <meta name="robots" content={robots} />
            {canonicalLink && <link rel="canonical" href={canonicalLink} />}
            {description && <meta name="description" content={description} />}
            {Object.keys(openGraph ?? {}).map((ogKey) => {
                if (!openGraph[ogKey]) return null;
                return <meta key={ogKey} property={ogKey} content={openGraph[ogKey]} />;
            })}
            <meta name="twitter:card" content={twitterCard} />
            {jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />}
            {children}
        </>
    );
};
